import BackupRounded from '@mui/icons-material/BackupRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { API_BASE, api, fetchBlob, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { dateTime } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import { SectionCard } from '../ui';

const INCLUDED = [
  'Products & stock',
  'Customers & suppliers',
  'Sales & purchases',
  'Payments & accounts',
  'Staff & roles',
  'Settings & numbering',
];

const sizeText = (bytes: number | null) =>
  bytes === null
    ? null
    : bytes < 1024 * 1024
      ? `${String(Math.max(1, Math.round(bytes / 1024)))} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/** Saves a blob through a temporary link (the response needs the session token, so no plain href). */
function save(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Settings → Backup: the owner downloads the whole shop as one file (restore = a new shop). */
export function BackupTab() {
  const [files, setFiles] = useState(true);
  const { me } = useAuth();
  const { format } = useShop();
  const notify = useNotify();
  const qc = useQueryClient();
  const history = useQuery({
    queryKey: ['backup', 'history'],
    queryFn: () => unwrap(api.GET('/backup/history')),
  });
  const download = useMutation({
    mutationFn: () => fetchBlob(`${API_BASE}/backup/download?files=${String(files)}`),
    onSuccess: (blob) => {
      const day = new Date().toISOString().slice(0, 10);
      save(blob, `${me?.tenant.slug ?? 'shop'}-${day}.ppbackup`);
      void qc.invalidateQueries({ queryKey: ['backup', 'history'] });
    },
    onError: (e) => notify.error(e),
  });
  const rows = history.data?.data ?? [];
  return (
    <Stack spacing={2.5}>
      <SectionCard
        icon={<BackupRounded />}
        title="Download a backup"
        description="The whole shop in one file. Keep it somewhere safe: anyone with the file can see your shop's data."
      >
        <Stack spacing={2}>
          <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
            {INCLUDED.map((x) => (
              <Chip
                key={x}
                icon={<CheckRounded />}
                label={x}
                size="small"
                color="success"
                variant="outlined"
              />
            ))}
          </Stack>
          <FormControlLabel
            control={<Checkbox checked={files} onChange={(e) => setFiles(e.target.checked)} />}
            label={
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  Include attachments and images
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                  Product photos and files attached to records. Makes the backup larger.
                </Typography>
              </Box>
            }
          />
          <Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<DownloadRounded />}
              loading={download.isPending}
              loadingPosition="start"
              onClick={() => download.mutate()}
            >
              {download.isPending ? 'Preparing backup…' : 'Download backup'}
            </Button>
          </Box>
          <Alert severity="info" icon={<InfoOutlined />}>
            To restore, create a new shop on the sign-up page and choose <b>Restore a backup</b>.
            Your current shop is not changed.
          </Alert>
        </Stack>
      </SectionCard>

      <SectionCard
        icon={<HistoryRounded />}
        title="Recent backups"
        description="Who downloaded a backup and when (also in the audit log)."
      >
        {history.isLoading ? (
          <Skeleton variant="rounded" height={56} />
        ) : rows.length === 0 ? (
          <Typography color="text.secondary" sx={{ fontSize: 14 }}>
            No backups downloaded yet.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {rows.map((b) => (
              <Stack key={b.at} direction="row" spacing={2} sx={{ py: 1.5, alignItems: 'center' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    {dateTime(b.at, format)}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                    {[
                      b.by.name ? `by ${b.by.name}` : null,
                      sizeText(b.bytes),
                      b.includesFiles ? 'with attachments' : 'data only',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      </SectionCard>
    </Stack>
  );
}
